// API Protection → Vulnerability Detection Overview — native (non-Superset) charts.
// Mirrors the API Protection Management overview (api-protection-overview-charts.ts)
// for Superset board 66 (scdn.v2_ApiVulnerability), with the same user-selectable
// Time Range (the dashboard's "time_range" template filter):
//   GET /api-protection/vulnerability/overview/charts?tz=&range= → { charts: WaapChart[] }
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';
import type { ApiProtectionTimeRange } from './api-protection-overview-charts';

/** Read the normalized Vulnerability Detection overview widgets for a website's
 *  account. `range` drives the dashboard's Time Range filter. Returns null when the
 *  board is not configured server-side (400/404/501) so the page can show a clear state. */
export function useWaapVulnerabilityOverviewCharts(
  domainId: string | undefined,
  range: ApiProtectionTimeRange,
) {
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.apiProtectionVulnOverviewCharts), range],
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const path = waapPath(domainId as string, WAAP_AREAS.apiProtectionVulnOverviewCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(
          `${path}?tz=${encodeURIComponent(tz)}&range=${encodeURIComponent(range)}`,
        );
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
